import React, { memo, useCallback, useRef, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import serialize from 'form-serialize';
import { useLocation } from 'react-router-dom';

import { AppContext } from '@prisjakt/ui-app-context';
import Rating from 'src/components/rating';
import { TextInput, TextArea } from 'src/components/inputs';
import T from '@prisjakt/ui-translate';
import Modal from '../../../../components/modal';
import SubmitButton from '../../../account/components/submit-button';

import * as S from './styles';

import {
  closeReviewDialog,
  createReview,
  editReview,
  deleteReview,
} from '../../actions';

import {
  productReviewCreated,
  productRated,
  productReviewDeleted,
  productReviewEdited,
} from '../../tracking';

import { AppState } from '../../../../reducers';
import { BelongsTo, Review } from '../../types';

function isValidDate(dateString: string) {
  const regEx = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateString.match(regEx)) return false; // Invalid format
  const d = new Date(dateString);
  if (!d.getTime() && d.getTime() !== 0) return false; // Invalid date
  return d.toISOString().slice(0, 10) === dateString;
}

const INITIAL_DRAFT = { belongsTo: null, rating: 0, text: '' };

const selectOpenId = (state: AppState) => state.areas.review.dialog.openId;
const selectRequestError = (state: AppState) => state.areas.review.requestError;
const selectPendingRequest = (state: AppState) =>
  state.areas.review.pendingRequest;
const selectSetDisplayName = (state: AppState) =>
  state.areas.account.me ? !state.areas.account.me.displayName : null;

type Props = {
  id: string;
  belongsTo: BelongsTo;
  review?: Review;
};

type ErrorState = {
  rating?: string;
  reviewText?: string;
  purchaseDate?: string;
  displayName?: string;
};

type DraftState = {
  belongsTo: string | number | null;
  rating: number;
  text: string;
};

const UserReviewDialog = ({ id, belongsTo, review }: Props) => {
  const dispatch = useDispatch();
  const { t, market } = useContext(AppContext);
  const formRef = useRef<HTMLFormElement>(null);
  const location = useLocation();

  const [errors, setErrors] = useState<ErrorState>({});
  const [reviewDraft, setReviewDraft] = useState<DraftState>(INITIAL_DRAFT);

  const openId = useSelector(selectOpenId);
  const requestError = useSelector(selectRequestError);
  const setDisplayName = useSelector(selectSetDisplayName);
  const pendingRequest = useSelector(selectPendingRequest);

  const type = belongsTo.type.toUpperCase();

  const saveReviewDraft = useCallback(() => {
    if (formRef.current) {
      const { reviewText, rating } = serialize(formRef.current, {
        hash: true,
      });

      setReviewDraft({
        belongsTo: belongsTo.id,
        text: reviewText,
        rating,
      });
    }
  }, [belongsTo.id]);

  const handleClose = useCallback(() => {
    saveReviewDraft();
    dispatch(closeReviewDialog());
  }, [dispatch, saveReviewDraft]);

  const setPurchaseDate =
    market.modules &&
    market.modules.userReviewPurchaseDate &&
    market.modules.userReviewPurchaseDate.enabled &&
    type === 'PRODUCT';

  const validators = useCallback(() => {
    return {
      rating: (rating: number) =>
        rating > 0 || t('reviews', 'dialog.rating-required-error'),

      reviewText: (reviewText: string) =>
        reviewText.trim().length > 0 ||
        t('reviews', 'dialog.text-required-error'),

      purchaseDate: (purchaseDate: string) => {
        if (!setPurchaseDate) {
          return true;
        }

        return (
          isValidDate(purchaseDate) || t('reviews', 'dialog.date-invalid-error')
        );
      },

      displayName: (displayName: string) =>
        !setDisplayName
          ? true
          : displayName.trim().length > 0 ||
            t('reviews', 'dialog.name-required-error'),
    };
  }, [t, setDisplayName, setPurchaseDate]);

  const validate = useCallback(
    (data: Record<string, string | number>) => {
      const fields = Object.keys(validators());
      const validateErrors = fields.reduce((err, key) => {
        const valueToValidate = data[key];
        const response = validators()[key](valueToValidate);

        return response === true ? err : { ...err, [key]: response };
      }, {});

      return {
        isValid: Object.keys(validateErrors).length === 0,
        validateErrors,
      };
    },
    [validators]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>, intent: string) => {
      e.preventDefault();
      const {
        reviewText = '',
        rating = 0,
        displayName = '',
        purchaseDate = '',
      } = serialize(e.target, {
        hash: true,
      });

      if (intent !== 'delete') {
        const { isValid, validateErrors } = validate({
          reviewText,
          rating,
          displayName,
          purchaseDate,
        });

        if (!isValid) {
          setErrors(validateErrors);
          return;
        }
      }

      switch (intent) {
        case 'create': {
          if (type === 'PRODUCT') {
            productReviewCreated();
            productRated(rating);
          }

          dispatch(
            createReview({
              rating,
              reviewText,
              displayName,
              purchaseDate: undefined,
              belongsToId: belongsTo.id,
              reviewType: type,
            })
          );
          break;
        }
        case 'edit': {
          if (!review) return;

          if (type === 'PRODUCT') {
            productReviewEdited();
          }

          dispatch(
            editReview({
              rating,
              reviewText,
              purchaseDate: undefined,
              reviewId: review.id,
              reviewType: type,
              belongsToId: belongsTo.id,
              location: location.pathname,
            })
          );
          break;
        }
        case 'delete': {
          if (!review) return;

          if (type === 'PRODUCT') {
            productReviewDeleted();
          }

          dispatch(
            deleteReview({
              reviewId: review.id,
              reviewType: type,
              belongsToId: belongsTo.id,
              location: location.pathname,
            })
          );
          break;
        }
        default:
          break;
      }
    },
    [belongsTo.id, dispatch, location.pathname, review, type, validate]
  );

  const handleCancel = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.preventDefault();
      handleClose();
    },
    [handleClose]
  );

  const clearErrors = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const inputName = e.target.name as InputName;
      const { [inputName]: remove, ...errorsToKeep } = errors;

      setErrors(errorsToKeep);
    },
    [errors]
  );

  const showSubmitSpinner =
    pendingRequest &&
    (pendingRequest.type === 'create' || pendingRequest.type === 'edit');
  const showDeleteSpinner = pendingRequest && pendingRequest.type === 'delete';
  const isOpen = openId === id;

  return isOpen ? (
    <Modal
      className="user-review-dialog"
      width={500}
      onRequestClose={handleClose}
      isOpen={isOpen}
      key="UserReviewDialogModal"
    >
      <S.Form
        ref={formRef}
        onSubmit={e => handleSubmit(e, review ? 'edit' : 'create')}
        method="POST"
      >
        <S.Title>{belongsTo.name}</S.Title>

        {requestError && (
          <S.Error>{t('reviews', 'dialog.unknown-error')}</S.Error>
        )}

        <S.Section>
          <S.InputTitle>{t('reviews', 'dialog.rate-info')}</S.InputTitle>

          <S.Rating error={Boolean(errors.rating)}>
            <Rating
              rate={review ? review.rating : reviewDraft.rating}
              interactive
              onChange={(_, e) => clearErrors(e)}
            />
          </S.Rating>

          {errors.rating && <S.Error>{errors.rating}</S.Error>}
        </S.Section>

        <S.Section>
          <TextArea
            name="reviewText"
            label={t('reviews', 'dialog.text-area-label')}
            tabIndex={0}
            helper={
              errors.reviewText || <T domain="reviews">dialog.rules.md</T>
            }
            warning={Boolean(errors.reviewText)}
            onChange={clearErrors}
            defaultValue={
              review ? (review.reviewText as string) : reviewDraft.text
            }
          />

          {setPurchaseDate && (
            <div>
              <S.InputTitle>
                {t('reviews', 'dialog.purchase-date')}
              </S.InputTitle>

              <input
                type="date"
                name="purchaseDate"
                placeholder={
                  market?.modules?.userReviewPurchaseDate?.datePlaceholderFormat
                }
                onChange={clearErrors}
                defaultValue={review?.purchaseDate || undefined}
              />

              {errors.purchaseDate && <S.Error>{errors.purchaseDate}</S.Error>}
            </div>
          )}
        </S.Section>

        {setDisplayName && (
          <S.Section>
            <TextInput
              name="displayName"
              label={t('reviews', 'dialog.display-name-label')}
              onChange={clearErrors}
              tabIndex={0}
              helper={
                errors.displayName
                  ? errors.displayName
                  : t('reviews', 'dialog.display-name-info')
              }
              warning={Boolean(errors.displayName)}
              type="text"
            />
          </S.Section>
        )}

        <S.Controls>
          {review && (
            <SubmitButton
              onClick={e => handleSubmit(e, 'delete')}
              loading={showDeleteSpinner}
              text={t('reviews', 'dialog.delete')}
            />
          )}

          <S.CancelButton
            onClick={handleCancel}
            color="primary"
            variant="outlined"
            alignRight={Boolean(review)}
          >
            {t('reviews', 'dialog.cancel')}
          </S.CancelButton>

          <SubmitButton
            loading={showSubmitSpinner}
            text={
              review
                ? t('reviews', 'dialog.update')
                : t('reviews', 'dialog.create')
            }
          />
        </S.Controls>
      </S.Form>
    </Modal>
  ) : null;
};

export default memo(UserReviewDialog);
type InputName = 'rating' | 'reviewText' | 'purchaseDate' | 'displayName';
